import type { LLMMessage, RegexRule, STPreset, WorldBookEntry } from '@/IsekaiRPG/modules/llm/llmTypes';
import { RegexProcessor } from '@/IsekaiRPG/modules/llm/llmRegexProcessor';
import type { SplitWorldBookEntries } from './types';

const PRIMARY_PROMPT_ORDER_CHARACTER_ID = 100001;

export class PromptAssembler {
  static assemble(
    runtimeMessages: LLMMessage[],
    preset: STPreset,
    splitWorldBook: SplitWorldBookEntries,
    generationType: string,
    options: {
      markerPayloads?: Record<string, string[]>;
      mainPromptOverride?: string | null;
      regexRules: RegexRule[];
      macroContext: Record<string, string>;
    },
  ): LLMMessage[] {
    const normalizedSplit = this.applyWorldInfoRegexToSplit(splitWorldBook, options.regexRules, options.macroContext);
    let merged = runtimeMessages;

    if (preset.enabled) {
      const mainOverride = options.mainPromptOverride?.trim() || undefined;
      const restMessages = runtimeMessages;
      const nodes = new Map(preset.prompts.map(node => [node.identifier, node]));
      const order = this.getPrimaryOrderEntries(preset);
      const { beforeChatHistory, afterChatHistory } = this.splitOrderAroundChatHistory(order);
      const markerInjection = this.buildMarkerInjectionPayload(order, nodes, normalizedSplit, options.markerPayloads);
      const presetMessagesBeforeChatHistory = this.buildRelativePresetMessages(
        beforeChatHistory,
        nodes,
        mainOverride,
        generationType,
        markerInjection.payloadByIdentifier,
        false,
      );
      const presetMessagesAfterChatHistory = this.buildRelativePresetMessages(
        afterChatHistory,
        nodes,
        mainOverride,
        generationType,
        markerInjection.payloadByIdentifier,
        false,
      );
      const runtimeWithAbsolutePrompts = this.injectAbsolutePrompts(
        order,
        nodes,
        restMessages,
        generationType,
        mainOverride,
      );
      const runtimeWithDepthWorldBook = this.injectWorldBookInChat(
        runtimeWithAbsolutePrompts,
        normalizedSplit.depthEntries,
      );
      const combinedPresetMessages =
        presetMessagesBeforeChatHistory.length === 0 &&
        presetMessagesAfterChatHistory.length === 0 &&
        mainOverride?.trim().length
          ? [{ role: 'system' as const, content: mainOverride.trim() }]
          : [];
      merged = [
        ...presetMessagesBeforeChatHistory,
        ...combinedPresetMessages,
        ...runtimeWithDepthWorldBook,
        ...presetMessagesAfterChatHistory,
      ];
      merged = [
        ...this.worldBookEntriesToMessages([
          ...markerInjection.unresolvedBefore,
          ...markerInjection.unresolvedBeforeExample,
          ...markerInjection.unresolvedBeforeAuthorNote,
        ]),
        ...merged,
        ...this.worldBookEntriesToMessages([
          ...markerInjection.unresolvedAfter,
          ...markerInjection.unresolvedAfterExample,
          ...markerInjection.unresolvedAfterAuthorNote,
          ...markerInjection.unresolvedOutlet,
        ]),
      ];
    } else {
      merged = this.injectWorldBookInChat(merged, normalizedSplit.depthEntries);
      merged = [
        ...this.worldBookEntriesToMessages(normalizedSplit.relativeBefore),
        ...this.worldBookEntriesToMessages(normalizedSplit.beforeExample),
        ...this.worldBookEntriesToMessages(normalizedSplit.beforeAuthorNote),
        ...merged,
        ...this.worldBookEntriesToMessages(normalizedSplit.relativeAfter),
        ...this.worldBookEntriesToMessages(normalizedSplit.afterExample),
        ...this.worldBookEntriesToMessages(normalizedSplit.afterAuthorNote),
        ...this.worldBookEntriesToMessages(normalizedSplit.outletEntries),
      ];
    }

    return merged;
  }

  private static applyWorldInfoRegexToSplit(
    split: SplitWorldBookEntries,
    regexRules: RegexRule[],
    macroContext: Record<string, string>,
  ): SplitWorldBookEntries {
    const mapEntries = (entries: WorldBookEntry[]): WorldBookEntry[] =>
      entries.map(entry => ({
        ...entry,
        content: RegexProcessor.applyForPlacement(entry.content ?? '', regexRules, 5, {
          isPrompt: true,
          depth: entry.injection_depth,
          macroContext,
        }),
      }));

    return {
      relativeBefore: mapEntries(split.relativeBefore),
      relativeAfter: mapEntries(split.relativeAfter),
      beforeExample: mapEntries(split.beforeExample),
      afterExample: mapEntries(split.afterExample),
      beforeAuthorNote: mapEntries(split.beforeAuthorNote),
      afterAuthorNote: mapEntries(split.afterAuthorNote),
      depthEntries: mapEntries(split.depthEntries),
      outletEntries: mapEntries(split.outletEntries),
    };
  }

  private static getPrimaryOrderEntries(preset: STPreset): STPreset['prompt_order'][number]['order'] {
    const preferredBlock = preset.prompt_order.find(block => block.character_id === PRIMARY_PROMPT_ORDER_CHARACTER_ID);
    return preferredBlock?.order ?? preset.prompt_order[0]?.order ?? [];
  }

  private static buildRelativePresetMessages(
    order: STPreset['prompt_order'][number]['order'],
    nodes: Map<string, STPreset['prompts'][number]>,
    mainOverride: string | undefined,
    generationType: string,
    markerPayloadByIdentifier: Map<string, string[]>,
    allowMainFallback = true,
  ): LLMMessage[] {
    const messages: LLMMessage[] = [];

    for (const entry of order) {
      if (!entry.enabled) continue;
      const node = nodes.get(entry.identifier);
      if (!node || this.isAbsoluteInjection(node) || !this.isTriggerMatched(node, generationType)) continue;

      if (node.marker) {
        const payload = markerPayloadByIdentifier.get(entry.identifier) ?? [];
        const payloadContent = payload
          .map(item => item.trim())
          .filter(Boolean)
          .join('\n');
        const markerContent = node.content?.trim() ?? '';
        const content =
          entry.identifier === 'gardenFlowerPrompt' && payloadContent
            ? payloadContent
            : [markerContent, payloadContent].filter(Boolean).join('\n').trim();

        if (content) {
          messages.push({ role: node.role, content });
        }
        continue;
      }

      let content = node.content?.trim() ?? '';
      if (entry.identifier === 'main' && mainOverride && mainOverride.trim().length > 0) {
        content = this.combineMainPromptContent(content, mainOverride);
      }

      if (!content) continue;
      messages.push({ role: node.role, content });
    }

    if (allowMainFallback && messages.length === 0 && mainOverride && mainOverride.trim().length > 0) {
      messages.push({ role: 'system', content: mainOverride.trim() });
    }

    return messages;
  }

  private static splitOrderAroundChatHistory(order: STPreset['prompt_order'][number]['order']): {
    beforeChatHistory: STPreset['prompt_order'][number]['order'];
    afterChatHistory: STPreset['prompt_order'][number]['order'];
  } {
    const chatHistoryIndex = order.findIndex(entry => entry.identifier === 'chatHistory');
    if (chatHistoryIndex === -1) {
      return {
        beforeChatHistory: order,
        afterChatHistory: [],
      };
    }

    return {
      beforeChatHistory: order.slice(0, chatHistoryIndex),
      afterChatHistory: order.slice(chatHistoryIndex + 1),
    };
  }

  private static injectAbsolutePrompts(
    order: STPreset['prompt_order'][number]['order'],
    nodes: Map<string, STPreset['prompts'][number]>,
    messages: LLMMessage[],
    generationType: string,
    mainOverride?: string,
  ): LLMMessage[] {
    const result = [...messages];
    const groupsByDepth = new Map<number, Array<{ role: LLMMessage['role']; content: string; order: number }>>();

    for (const entry of order) {
      if (!entry.enabled) continue;
      const node = nodes.get(entry.identifier);
      if (!node || node.marker || !this.isAbsoluteInjection(node) || !this.isTriggerMatched(node, generationType))
        continue;

      let content = node.content?.trim() ?? '';
      if (entry.identifier === 'main' && mainOverride && mainOverride.trim().length > 0) {
        content = this.combineMainPromptContent(content, mainOverride);
      }
      if (!content) continue;

      const depth = Math.max(0, node.injection_depth ?? 4);
      const current = groupsByDepth.get(depth) ?? [];
      current.push({
        role: node.role,
        content,
        order: node.injection_order ?? 100,
      });
      groupsByDepth.set(depth, current);
    }

    const depths = [...groupsByDepth.keys()].sort((a, b) => b - a);
    for (const depth of depths) {
      const group = groupsByDepth.get(depth);
      if (!group || group.length === 0) continue;

      const roleMessages = this.buildChronologicalInChatMessages(group);
      if (roleMessages.length === 0) continue;

      const insertAt = Math.max(0, result.length - depth);
      result.splice(insertAt, 0, ...roleMessages);
    }

    return result;
  }

  private static buildChronologicalInChatMessages(
    group: Array<{ role: LLMMessage['role']; content: string; order: number }>,
  ): LLMMessage[] {
    // 对齐酒馆最终发送给模型时的顺序：
    // 1. 先按 injection_order 从低到高排列桶
    // 2. 每个桶内部按 assistant -> user -> system 输出
    // 这样 system 会更靠近底部，保持“更重要的信息更靠近当前生成”的语义。
    const roles: LLMMessage['role'][] = ['assistant', 'user', 'system'];
    const messages: LLMMessage[] = [];
    const orderBuckets = [...new Set(group.map(item => item.order))].sort((a, b) => a - b);

    for (const order of orderBuckets) {
      const orderGroup = group.filter(item => item.order === order);

      for (const role of roles) {
        const content = orderGroup
          .filter(item => item.role === role)
          .map(item => item.content.trim())
          .filter(Boolean)
          .join('\n');

        if (!content) continue;
        messages.push({ role, content });
      }
    }

    return messages;
  }

  private static isAbsoluteInjection(node: STPreset['prompts'][number]): boolean {
    return node.injection_position === 1;
  }

  private static isTriggerMatched(node: STPreset['prompts'][number], generationType: string): boolean {
    const triggerList = node.injection_trigger;
    if (!Array.isArray(triggerList) || triggerList.length === 0) {
      return true;
    }

    const triggerSet = new Set(triggerList.map(trigger => String(trigger).trim().toLowerCase()).filter(Boolean));
    const compatibleGenerationTypes = this.getCompatibleGenerationTypes(generationType);
    return compatibleGenerationTypes.some(type => triggerSet.has(type));
  }

  private static getCompatibleGenerationTypes(generationType: string): string[] {
    const normalized = generationType.trim().toLowerCase();
    if (!normalized) {
      return ['normal', 'chat'];
    }

    // 项目内部普通对话一直用 chat，但酒馆预设的普通生成触发器通常写成 normal，这里视为同义。
    if (normalized === 'chat' || normalized === 'normal') {
      return ['chat', 'normal'];
    }

    return [normalized];
  }

  private static combineMainPromptContent(baseContent: string, mainOverride: string): string {
    const normalizedBase = baseContent.trim();
    const normalizedOverride = mainOverride.trim();
    return [normalizedBase, normalizedOverride].filter(Boolean).join('\n');
  }

  private static buildMarkerInjectionPayload(
    order: STPreset['prompt_order'][number]['order'],
    nodes: Map<string, STPreset['prompts'][number]>,
    splitEntries: SplitWorldBookEntries,
    markerPayloads?: Record<string, string[]>,
  ): {
    payloadByIdentifier: Map<string, string[]>;
    unresolvedBefore: WorldBookEntry[];
    unresolvedAfter: WorldBookEntry[];
    unresolvedBeforeExample: WorldBookEntry[];
    unresolvedAfterExample: WorldBookEntry[];
    unresolvedBeforeAuthorNote: WorldBookEntry[];
    unresolvedAfterAuthorNote: WorldBookEntry[];
    unresolvedOutlet: WorldBookEntry[];
  } {
    const payloadByIdentifier = new Map<string, string[]>();
    const unresolvedBefore = this.routeEntriesToMarker(
      splitEntries.relativeBefore,
      this.findMarkerIdentifier(order, nodes, 'before'),
      payloadByIdentifier,
    );
    const unresolvedAfter = this.routeEntriesToMarker(
      splitEntries.relativeAfter,
      this.findMarkerIdentifier(order, nodes, 'after'),
      payloadByIdentifier,
    );
    const unresolvedBeforeExample = this.routeEntriesToMarker(
      splitEntries.beforeExample,
      this.findMarkerIdentifier(order, nodes, 'before_example'),
      payloadByIdentifier,
    );
    const unresolvedAfterExample = this.routeEntriesToMarker(
      splitEntries.afterExample,
      this.findMarkerIdentifier(order, nodes, 'after_example'),
      payloadByIdentifier,
    );
    const unresolvedBeforeAuthorNote = this.routeEntriesToMarker(
      splitEntries.beforeAuthorNote,
      this.findMarkerIdentifier(order, nodes, 'before_author_note'),
      payloadByIdentifier,
    );
    const unresolvedAfterAuthorNote = this.routeEntriesToMarker(
      splitEntries.afterAuthorNote,
      this.findMarkerIdentifier(order, nodes, 'after_author_note'),
      payloadByIdentifier,
    );

    const unresolvedOutlet: WorldBookEntry[] = [];
    for (const entry of splitEntries.outletEntries) {
      const markerId = this.findOutletIdentifier(order, nodes, entry.outlet_name);
      if (!markerId) {
        unresolvedOutlet.push(entry);
        continue;
      }
      this.pushMarkerPayload(payloadByIdentifier, markerId, entry);
    }

    for (const [markerId, payloadItems] of Object.entries(markerPayloads ?? {})) {
      const normalizedItems = payloadItems.map(item => item.trim()).filter(Boolean);
      if (normalizedItems.length === 0) continue;

      const current = payloadByIdentifier.get(markerId) ?? [];
      current.push(...normalizedItems);
      payloadByIdentifier.set(markerId, current);
    }

    return {
      payloadByIdentifier,
      unresolvedBefore,
      unresolvedAfter,
      unresolvedBeforeExample,
      unresolvedAfterExample,
      unresolvedBeforeAuthorNote,
      unresolvedAfterAuthorNote,
      unresolvedOutlet,
    };
  }

  private static routeEntriesToMarker(
    entries: WorldBookEntry[],
    markerId: string | null,
    payloadByIdentifier: Map<string, string[]>,
  ): WorldBookEntry[] {
    if (!markerId) return entries;
    for (const entry of entries) {
      this.pushMarkerPayload(payloadByIdentifier, markerId, entry);
    }
    return [];
  }

  private static pushMarkerPayload(
    payloadByIdentifier: Map<string, string[]>,
    markerId: string,
    entry: WorldBookEntry,
  ): void {
    const current = payloadByIdentifier.get(markerId) ?? [];
    const content = entry.content.trim();
    if (!content) return;
    current.push(content);
    payloadByIdentifier.set(markerId, current);
  }

  private static findMarkerIdentifier(
    order: STPreset['prompt_order'][number]['order'],
    nodes: Map<string, STPreset['prompts'][number]>,
    mode: 'before' | 'after' | 'before_example' | 'after_example' | 'before_author_note' | 'after_author_note',
  ): string | null {
    const aliasesMap: Record<typeof mode, string[]> = {
      before: ['worldinfobefore', 'wi_before', 'before_char', 'beforechar'],
      after: ['worldinfoafter', 'wi_after', 'after_char', 'afterchar'],
      before_example: ['before_example', 'beforeexample', 'wi_before_example', 'wi_beforeexample'],
      after_example: ['after_example', 'afterexample', 'wi_after_example', 'wi_afterexample'],
      before_author_note: ['before_an', 'before_author_note', 'wi_before_an', 'wi_before_author_note'],
      after_author_note: ['after_an', 'after_author_note', 'wi_after_an', 'wi_after_author_note'],
    };
    const aliases = aliasesMap[mode];

    for (const entry of order) {
      if (!entry.enabled) continue;
      const node = nodes.get(entry.identifier);
      if (!node?.marker) continue;

      const searchText = `${entry.identifier} ${node.name ?? ''}`.trim().toLowerCase();
      if (aliases.some(alias => searchText.includes(alias))) {
        return entry.identifier;
      }
    }

    return null;
  }

  private static findOutletIdentifier(
    order: STPreset['prompt_order'][number]['order'],
    nodes: Map<string, STPreset['prompts'][number]>,
    outletName: string,
  ): string | null {
    const normalizedOutlet = outletName?.trim().toLowerCase();
    if (!normalizedOutlet) return null;

    for (const entry of order) {
      if (!entry.enabled) continue;
      const node = nodes.get(entry.identifier);
      if (!node?.marker) continue;

      const identifier = entry.identifier.trim().toLowerCase();
      const name = (node.name ?? '').trim().toLowerCase();
      if (identifier === normalizedOutlet || name === normalizedOutlet) {
        return entry.identifier;
      }
    }

    return null;
  }

  private static injectWorldBookInChat(messages: LLMMessage[], entries: WorldBookEntry[]): LLMMessage[] {
    const result = [...messages];
    const groupsByDepth = new Map<number, Array<{ role: LLMMessage['role']; content: string; order: number }>>();

    for (const entry of entries) {
      const content = entry.content.trim();
      if (!content) continue;

      const depth = Math.max(0, entry.injection_depth ?? 2);
      const current = groupsByDepth.get(depth) ?? [];
      current.push({
        role: entry.role,
        content,
        order: entry.injection_order ?? 100,
      });
      groupsByDepth.set(depth, current);
    }

    const depths = [...groupsByDepth.keys()].sort((a, b) => b - a);
    for (const depth of depths) {
      const group = groupsByDepth.get(depth);
      if (!group || group.length === 0) continue;

      const roleMessages = this.buildChronologicalInChatMessages(group);
      if (roleMessages.length === 0) continue;

      const insertAt = Math.max(0, result.length - depth);
      result.splice(insertAt, 0, ...roleMessages);
    }

    return result;
  }

  private static worldBookEntriesToMessages(entries: WorldBookEntry[]): LLMMessage[] {
    return entries
      .map(entry => ({
        role: entry.role,
        content: entry.content.trim(),
      }))
      .filter(entry => entry.content.length > 0);
  }
}
